import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000,
  withCredentials: true,
});

// Auth
export const loginUser = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const logoutUser = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

export const refreshToken = async () => {
  const response = await api.post('/auth/refresh');
  return response.data;
};

// Password Setup
export const sendSetupEmails = async (userIds) => {
  const response = await api.post('/auth/send-setup-emails', { user_ids: userIds });
  return response.data;
};

export const validateSetupToken = async (token) => {
  const response = await api.get(`/auth/setup-token/${token}`);
  return response.data;
};

export const setPassword = async (token, password) => {
  const response = await api.post('/auth/set-password', { token, password });
  return response.data;
};

// Users
export const listUsers = async (status = 'active', userType = 'all') => {
  const response = await api.get(`/users?status=${status}&user_type=${userType}`);
  return response.data;
};

export const createUser = async (data) => {
  const response = await api.post('/users', data);
  return response.data;
};

export const updateUser = async (userId, data) => {
  const response = await api.put(`/users/${userId}`, data);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

export const restoreUser = async (userId) => {
  const response = await api.post(`/users/${userId}/restore`);
  return response.data;
};

export const importUsersCSV = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/users/import-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const importUsersFromATS = async (region) => {
  const response = await api.post('/ats-settings/users/import', { region });
  return response.data;
};

export const getUserCounts = async () => {
  const response = await api.get('/users/counts');
  return response.data;
};

// API Tokens
export const listAPITokens = async (userId) => {
  const params = userId ? `?user_id=${userId}` : '';
  const response = await api.get(`/api-tokens${params}`);
  return response.data;
};

export const createAPIToken = async (data) => {
  const response = await api.post('/api-tokens', data);
  return response.data;
};

export const revokeAPIToken = async (tokenId) => {
  const response = await api.delete(`/api-tokens/${tokenId}`);
  return response.data;
};

// ATS Settings
export const listATSSettings = async () => {
  const response = await api.get('/ats-settings');
  return response.data;
};

export const saveATSSettings = async (data) => {
  const response = await api.post('/ats-settings', data);
  return response.data;
};

export const testATSConnection = async (region) => {
  const response = await api.post('/ats-settings/test', { region });
  return response.data;
};

export const deleteATSSettings = async (region) => {
  const response = await api.delete(`/ats-settings/${region}`);
  return response.data;
};

// Ceipal auto-import
export const getAutoImportStatus = async (region) => (await api.get(`/ats/auto-import/${region}`)).data;
export const setAutoImportEnabled = async (region, enabled) => (await api.put(`/ats/auto-import/${region}`, { enabled })).data;
export const runAutoImport = async (region) => (await api.post(`/ats/auto-import/${region}/run`)).data;
export const getAutoImportRuns = async (region) => (await api.get(`/ats/auto-import/${region}/runs`)).data;

// ATS Job Import
export const searchATSJobs = async (region, search = '', page = 1, mode = 'browse') => {
  const response = await api.get(`/ats-jobs/search?region=${region}&search=${encodeURIComponent(search)}&page=${page}&mode=${mode}`);
  return response.data;
};

export const importATSJob = async (region, atsId) => {
  const response = await api.post('/ats-jobs/import', { region, ats_id: atsId });
  return response.data;
};

// ATS Candidate Import
export const searchATSCandidates = async (region, search = '', page = 1) => {
  const response = await api.get(`/ats-candidates/search?region=${region}&search=${encodeURIComponent(search)}&page=${page}`);
  return response.data;
};

export const importATSCandidate = async (region, atsId, jobId) => {
  const response = await api.post('/ats-candidates/import', { region, ats_id: atsId, job_id: jobId });
  return response.data;
};

// Jobs
export const createJob = async (jobData) => {
  const response = await api.post('/jobs', jobData);
  return response.data;
};

export const updateJob = async (jobId, updateData) => {
  const response = await api.put(`/jobs/${jobId}`, updateData);
  return response.data;
};

export const mergeJobs = async (targetJobId, sourceJobIds) => {
  const response = await api.post('/jobs/merge', {
    target_job_id: targetJobId,
    source_job_ids: sourceJobIds
  });
  return response.data;
};

export const getJobs = async (region) => {
  const params = region ? { region } : {};
  const response = await api.get('/jobs', { params });
  return response.data;
};

export const getJob = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}`);
  return response.data;
};

export const deleteJob = async (jobId) => {
  const response = await api.delete(`/jobs/${jobId}`);
  return response.data;
};

// JD Analysis
export const analyzeJD = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/analyze-jd`, {}, { timeout: 120000 });
  return response.data;
};

// Resume Upload - Upload in batches of 50 for reliability
export const uploadResumes = async (jobId, files, onProgress) => {
  const BATCH_SIZE = 50;
  const totalFiles = files.length;
  let totalProcessed = 0;
  let totalFailed = 0;
  let totalDuplicates = 0;
  
  // Split files into batches
  const batches = [];
  for (let i = 0; i < files.length; i += BATCH_SIZE) {
    batches.push(files.slice(i, i + BATCH_SIZE));
  }
  
  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    const batch = batches[batchIndex];
    const formData = new FormData();
    batch.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const response = await api.post(`/jobs/${jobId}/resumes`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes per batch
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            // Calculate overall progress based on batch completion
            const batchProgress = (progressEvent.loaded / progressEvent.total) * 100;
            const overallProgress = Math.round(
              ((batchIndex * BATCH_SIZE + (batch.length * batchProgress / 100)) / totalFiles) * 100
            );
            onProgress(Math.min(overallProgress, 99)); // Cap at 99 until fully done
          }
        },
      });
      
      totalProcessed += response.data.processed || 0;
      totalFailed += response.data.failed || 0;
      totalDuplicates += response.data.duplicates || 0;
    } catch (error) {
      console.error(`Batch ${batchIndex + 1} failed:`, error);
      totalFailed += batch.length;
    }
  }
  
  if (onProgress) {
    onProgress(100);
  }
  
  return {
    processed: totalProcessed,
    failed: totalFailed,
    duplicates: totalDuplicates
  };
};

// Resume Analysis
export const analyzeResumes = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/analyze-resumes`);
  return response.data;
};

// Get Candidates
export const getCandidates = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/candidates`);
  return response.data;
};

export const getAllCandidates = async (region, search = '', limit = 500, skip = 0, jobId = null) => {
  const params = new URLSearchParams({ search, limit, skip });
  if (region) params.append('region', region);
  if (jobId) params.append('job_id', jobId);
  const response = await api.get(`/candidates/all?${params}`);
  return response.data;
};


export const getCandidateDetail = async (jobId, candidateId) => {
  const response = await api.get(`/jobs/${jobId}/candidate/${candidateId}`);
  return response.data;
};

export const updateCandidate = async (jobId, candidateId, updateData) => {
  const response = await api.put(`/jobs/${jobId}/candidates/${candidateId}`, updateData);
  return response.data;
};


// Progress
export const getJobProgress = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/progress`);
  return response.data;
};

// Export
export const exportCandidates = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/export`, {
    responseType: 'blob',
  });
  return response.data;
};

// Top Candidates (Auto-shortlist)
export const getTopCandidates = async (jobId, limit = 10) => {
  const response = await api.get(`/jobs/${jobId}/top-candidates?limit=${limit}`);
  return response.data;
};

// Location Heatmap
export const getLocationHeatmap = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/location-heatmap`);
  return response.data;
};

// Candidate Clusters
export const getCandidateClusters = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/clusters`);
  return response.data;
};

// Compare Candidates
export const compareCandidates = async (jobId, candidateIds) => {
  const response = await api.post(`/jobs/${jobId}/compare`, candidateIds);
  return response.data;
};

// ================== AI INTERVIEW APIs ==================

// Generate interview questions
export const generateInterviewQuestions = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/generate-questions`, {}, { timeout: 120000 });
  return response.data;
};

// Get interview questions
export const getInterviewQuestions = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/questions`);
  return response.data;
};

// Update interview questions
export const updateInterviewQuestions = async (jobId, questions) => {
  const response = await api.put(`/jobs/${jobId}/questions`, { questions });
  return response.data;
};

// Suggest coding questions via AI
export const suggestCodingQuestions = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/suggest-coding-questions`, {}, { timeout: 120000 });
  return response.data;
};

// ==================== EMAIL API ====================

// Save email SMTP configuration
export const saveEmailConfig = async (config) => {
  const response = await api.post('/email/config', config);
  return response.data;
};

// Get email configuration (masked password)
export const getEmailConfig = async (region = 'global') => {
  const response = await api.get(`/email/config/${region}`);
  return response.data;
};

// Test email configuration
export const testEmailConfig = async (config) => {
  const response = await api.post('/email/test', config, { timeout: 30000 });
  return response.data;
};

// Send interview email
export const sendInterviewEmail = async (emailData) => {
  const response = await api.post('/email/send-interview', emailData, { timeout: 30000 });
  return response.data;
};

// Get email history
export const getEmailHistory = async (params = {}) => {
  const response = await api.get('/email/history', { params });
  return response.data;
};

// Get email stats
export const getEmailStats = async (region) => {
  const response = await api.get('/email/stats', { params: region ? { region } : {} });
  return response.data;
};

// Generate interview link for candidate
export const generateInterviewLink = async (jobId, candidateId) => {
  const response = await api.post(`/jobs/${jobId}/candidates/${candidateId}/interview-link`);
  return response.data;
};

// Get candidate interviews
export const getCandidateInterviews = async (jobId, candidateId) => {
  const response = await api.get(`/jobs/${jobId}/candidates/${candidateId}/interviews`);
  return response.data;
};

// Get interview session (public)
export const getInterviewSession = async (token) => {
  const response = await api.get(`/interview/${token}`);
  return response.data;
};

// Start interview
export const startInterview = async (token, data) => {
  const response = await api.post(`/interview/${token}/start`, data);
  return response.data;
};

// Submit answer
export const submitAnswer = async (token, data) => {
  const response = await api.post(`/interview/${token}/submit-answer`, data, { timeout: 60000 });
  return response.data;
};

// Report proctoring violation
export const reportViolation = async (token, violationType = 'tab_switch', details = '') => {
  const response = await api.post(`/interview/${token}/violation`, null, {
    params: { violation_type: violationType, details }
  });
  return response.data;
};

export const reportInterviewIssue = async (token, issueData) => {
  const response = await api.post(`/interviews/${token}/report-issue`, issueData);
  return response.data;
};


// Complete interview
export const completeInterview = async (token) => {
  const response = await api.post(`/interview/${token}/complete`, {}, { timeout: 60000 });
  return response.data;
};

export const uploadInterviewVideo = async (token, blob, questionIndex) => {
  const formData = new FormData();
  formData.append('file', blob, `q${questionIndex}.webm`);
  formData.append('question_index', questionIndex);
  const response = await api.post(`/interview/${token}/upload-video`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000
  });
  return response.data;
};

// Get interview results
export const getInterviewResults = async (token) => {
  const response = await api.get(`/interview/${token}/results`);
  return response.data;
};

export const recomputeInterviewAnalysis = async (token) => {
  const response = await api.post(`/interview/${token}/recompute`);
  return response.data;
};


// Export interview report
export const exportInterviewReport = async (jobId, candidateId, format = 'pdf') => {
  const response = await api.get(`/jobs/${jobId}/candidates/${candidateId}/interview-report?format=${format}`, {
    responseType: (format === 'docx' || format === 'pdf') ? 'blob' : 'json'
  });
  return response.data;
};

// ================== CANDIDATE NOTES APIs ==================

export const getCandidateNotes = async (jobId, candidateId) => {
  const response = await api.get(`/jobs/${jobId}/candidates/${candidateId}/notes`);
  return response.data;
};

export const addCandidateNote = async (jobId, candidateId, text) => {
  const response = await api.post(`/jobs/${jobId}/candidates/${candidateId}/notes`, { text });
  return response.data;
};

export const deleteCandidateNote = async (jobId, candidateId, noteId) => {
  const response = await api.delete(`/jobs/${jobId}/candidates/${candidateId}/notes/${noteId}`);
  return response.data;
};

// ================== INTERVIEW HISTORY ==================

export const getInterviewHistory = async (search = '', status = 'all', limit = 50, skip = 0, region = null) => {
  const params = new URLSearchParams({ search, status, limit, skip });
  if (region) params.append('region', region);
  const response = await api.get(`/interviews/history?${params}`);
  return response.data;
};

// Notifications
export const getNotifications = async (limit = 100, unreadOnly = false, region = null) => {
  const params = new URLSearchParams({ limit, unread_only: unreadOnly });
  if (region) params.append('region', region);
  const response = await api.get(`/notifications?${params}`);
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await api.post(`/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await api.post(`/notifications/read-all`);
  return response.data;
};

export const deleteNotification = async (notificationId) => {
  const response = await api.delete(`/notifications/${notificationId}`);
  return response.data;
};

export const clearAllNotifications = async () => {
  const response = await api.delete(`/notifications`);
  return response.data;
};

// ================== REGION MANAGEMENT ==================

export const getRegionStats = async () => {
  const response = await api.get('/regions/stats');
  return response.data;
};

export const moveJobRegion = async (jobId, targetRegion) => {
  const response = await api.post(`/jobs/${jobId}/move-region`, { target_region: targetRegion });
  return response.data;
};

export const bulkMoveJobsRegion = async (jobIds, targetRegion) => {
  const response = await api.post('/jobs/bulk-move-region', { job_ids: jobIds, target_region: targetRegion });
  return response.data;
};

// ================== SOFT DELETE & RESTORE ==================

export const getDeletedJobs = async (region) => {
  const params = region ? { region, include_deleted: true } : { include_deleted: true };
  const response = await api.get('/jobs', { params });
  return response.data;
};

export const restoreJob = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/restore`);
  return response.data;
};

export const permanentDeleteJob = async (jobId) => {
  const response = await api.delete(`/jobs/${jobId}`, { params: { permanent: true } });
  return response.data;
};

export const getDeletedCandidates = async (region) => {
  const params = region ? { region } : {};
  const response = await api.get('/deleted/candidates', { params });
  return response.data;
};

export const restoreCandidate = async (candidateId) => {
  const response = await api.post(`/candidates/${candidateId}/restore`);
  return response.data;
};

export const permanentDeleteCandidate = async (candidateId) => {
  const response = await api.delete(`/candidates/${candidateId}`, { params: { permanent: true } });
  return response.data;
};

export const getDeletedStats = async (region) => {
  const params = region ? { region } : {};
  const response = await api.get('/deleted/stats', { params });
  return response.data;
};

// ================== AUDIT LOGS ==================

export const getAuditLogs = async (params = {}) => {
  const response = await api.get('/audit-logs', { params });
  return response.data;
};

export const getAuditSummary = async (region) => {
  const params = region ? { region } : {};
  const response = await api.get('/audit-logs/summary', { params });
  return response.data;
};

// ================== PRESENTATIONS ==================

export const getPresentations = async () => {
  const response = await api.get('/presentations');
  return response.data;
};

export const downloadPresentation = (filename) => {
  return `${API_BASE}/presentations/${encodeURIComponent(filename)}/download`;
};

// ================== VOICE CALIBRATION ==================

export const saveVoiceCalibration = async (token, calibrationData) => {
  const response = await api.post(`/interview/${token}/calibration`, calibrationData);
  return response.data;
};

export const getVoiceCalibration = async (token) => {
  const response = await api.get(`/interview/${token}/calibration`);
  return response.data;
};

// ================== SHARE RESULTS ==================

export const createShareLink = async (token, expiresInDays = 7) => {
  const response = await api.post(`/interview/${token}/share`, { expires_in_days: expiresInDays });
  return response.data;
};

export const getSharedResults = async (shareToken) => {
  const response = await api.get(`/shared/${shareToken}`);
  return response.data;
};

// ================== CANDIDATE TAGS ==================

export const getTags = async (region) => {
  const params = region ? { region } : {};
  const response = await api.get('/tags', { params });
  return response.data;
};

export const createTag = async (name, color, region = 'global') => {
  const response = await api.post(`/tags?region=${region}`, { name, color });
  return response.data;
};

export const deleteTag = async (tagId) => {
  const response = await api.delete(`/tags/${tagId}`);
  return response.data;
};

export const assignCandidateTags = async (jobId, candidateId, tagIds) => {
  const response = await api.post(`/jobs/${jobId}/candidates/${candidateId}/tags`, { tag_ids: tagIds });
  return response.data;
};

export const getCandidateTags = async (jobId, candidateId) => {
  const response = await api.get(`/jobs/${jobId}/candidates/${candidateId}/tags`);
  return response.data;
};

// ================== PDF EXPORT ==================

export const exportPdfReport = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/export-pdf`, { responseType: 'blob' });
  return response.data;
};

// ================== BATCH PROCESSING ==================

export const startBatchAnalysis = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/analyze-resumes-batch`);
  return response.data;
};

export const getBatchProgress = async (batchId) => {
  const response = await api.get(`/batch/${batchId}/progress`);
  return response.data;
};

export const getJobBatchStatus = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/batch-status`);
  return response.data;
};

// ================== PUBLIC TEST LINK ==================

export const generatePublicLink = async (jobId, expiresInDays = null, resumeRequired = true) => {
  const params = {};
  if (expiresInDays) params.expires_in_days = expiresInDays;
  params.resume_required = resumeRequired;
  const response = await api.post(`/jobs/${jobId}/public-link`, null, { params });
  return response.data;
};

export const getPublicLink = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/public-link`);
  return response.data;
};

export const disablePublicLink = async (jobId) => {
  const response = await api.delete(`/jobs/${jobId}/public-link`);
  return response.data;
};

export const getPublicTestInfo = async (token) => {
  const response = await api.get(`/public-test/${token}`);
  return response.data;
};

export const submitPublicTestApplication = async (token, formData) => {
  const response = await api.post(`/public-test/${token}/apply`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

export const getPublicTestAnalytics = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/public-test-analytics`);
  return response.data;
};


// Market Intelligence
export const generateMIReport = async (data) => {
  const response = await api.post('/mi/generate-report', data);
  return response.data;
};

export const getMIJobStatus = async (jobId) => {
  const response = await api.get(`/mi/job-status/${jobId}`);
  return response.data;
};

export const getMIReport = async (reportId) => {
  const response = await api.get(`/mi/report/${reportId}`);
  return response.data;
};

export const getMIReportByJob = async (hirefastJobId) => {
  const response = await api.get(`/mi/job-report/${hirefastJobId}`);
  return response.data;
};

export const createMIShareLink = async (jobId) => {
  const response = await api.post(`/mi/share/${jobId}`);
  return response.data;
};

export const createJDShareLink = async (jobId) => {
  const response = await api.post(`/jd/share/${jobId}`);
  return response.data;
};

export const getSharedMIReport = async (shareToken) => {
  const response = await api.get(`/shared/mi/${shareToken}`);
  return response.data;
};

export const getSharedJDAnalysis = async (shareToken) => {
  const response = await api.get(`/shared/jd/${shareToken}`);
  return response.data;
};

// Discovery Questions
export const generateDiscoveryQuestions = async (data) => {
  const response = await api.post('/discovery/generate', data);
  return response.data;
};

export const getDiscoveryJobStatus = async (jobId) => {
  const response = await api.get(`/discovery/job-status/${jobId}`);
  return response.data;
};

export const getDiscoveryReportByJob = async (hirefastJobId) => {
  const response = await api.get(`/discovery/job-report/${hirefastJobId}`);
  return response.data;
};

export const createDiscoveryShareLink = async (jobId) => {
  const response = await api.post(`/discovery/share/${jobId}`);
  return response.data;
};

export const getSharedDiscoveryReport = async (shareToken) => {
  const response = await api.get(`/shared/discovery/${shareToken}`);
  return response.data;
};

// Sourcing Strategy
export const generateSourcingPlan = async (data) => {
  const response = await api.post('/sourcing/generate-plan', data);
  return response.data;
};

export const getSourcingJobStatus = async (jobId) => {
  const response = await api.get(`/sourcing/job-status/${jobId}`);
  return response.data;
};

export const getSourcingReportByJob = async (hirefastJobId) => {
  const response = await api.get(`/sourcing/job-report/${hirefastJobId}`);
  return response.data;
};

export const createSourcingShareLink = async (jobId) => {
  const response = await api.post(`/sourcing/share/${jobId}`);
  return response.data;
};

export const getSharedSourcingPlan = async (shareToken) => {
  const response = await api.get(`/shared/sourcing/${shareToken}`);
  return response.data;
};

// Combined Share
export const checkCombinedSections = async (jobId) => {
  const response = await api.get(`/combined-share/check/${jobId}`);
  return response.data;
};

export const createCombinedShareLink = async (jobId, sections) => {
  const response = await api.post(`/combined-share/${jobId}`, { sections });
  return response.data;
};

export const getSharedCombined = async (shareToken) => {
  const response = await api.get(`/shared/combined/${shareToken}`);
  return response.data;
};

export default api;
